import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TransferAndRolloverNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Helper function to extract extension ID from various possible field names.
 * Checks fields in priority order and returns the first valid value.
 */
const extractExtensionId = (
  record: Record<string, unknown>,
  fieldNames: string[],
): unknown => {
  for (const fieldName of fieldNames) {
    const value = record[fieldName]
    if (value !== undefined && value !== null && value !== '' && value !== 0) {
      return value
    }
  }
  return null
}

/**
 * Helper function to clean up legacy extension fields from node data.
 */
const cleanupLegacyExtensionFields = (
  updatedData: Record<string, unknown>,
): void => {
  delete updatedData.extensionId
  delete updatedData.extension_id
  delete updatedData.transfer_rollover_id
}

/**
 * Hook for transfer and rollover node functionality.
 * Handles extensions array and timeout updates.
 */
export const useTransferAndRolloverNode = (
  id: string,
  data: TransferAndRolloverNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Local state for timeout input (to prevent focus loss)
  const [timeout, setTimeoutValue] = useState(
    data.timeout !== undefined ? String(data.timeout) : '',
  )

  // Sync local state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setTimeoutValue(data.timeout !== undefined ? String(data.timeout) : '')
    }
  }, [data.timeout])

  const currentExtensions = useMemo(() => {
    const dataRecord = data as Record<string, unknown>

    // If extensions array exists, use it
    if (
      data.extensions &&
      Array.isArray(data.extensions) &&
      data.extensions.length > 0
    ) {
      return data.extensions
        .map((ext) => String(ext))
        .filter((ext) => ext && ext !== '')
    }

    // Fallback: check for various extension ID field names (legacy formats)
    const extensionId = extractExtensionId(dataRecord, [
      'transfer_rollover_id', // Specific to transfer_rollover nodes
      'extensionId',
      'extension_id',
    ])

    return extensionId ? [String(extensionId)] : []
  }, [data])

  const handleExtensionChange = useCallback(
    (extensionId: string | undefined) => {
      if (!extensionId) {
        // If cleared, remove the first extension
        if (currentExtensions.length > 0) {
          const newExtensions = currentExtensions.slice(1)
          const updatedData = {
            ...data,
            extensions: newExtensions.length > 0 ? newExtensions : undefined,
          }
          cleanupLegacyExtensionFields(updatedData)
          updateNodeData(id, updatedData)
        }
        return
      }

      // Replace the first extension or add it if array is empty
      const otherExtensions = currentExtensions.slice(1)
      const newExtensions = [
        extensionId,
        ...otherExtensions.filter((ext) => ext !== extensionId),
      ]

      const updatedData = { ...data, extensions: newExtensions }
      cleanupLegacyExtensionFields(updatedData)
      updateNodeData(id, updatedData)
    },
    [id, data, currentExtensions, updateNodeData],
  )

  const handleAddExtension = useCallback(
    (extensionId: string | undefined) => {
      if (!extensionId) return

      const newExtensions = [
        ...currentExtensions.filter((ext) => ext !== extensionId),
        extensionId,
      ]

      const updatedData = { ...data, extensions: newExtensions }
      cleanupLegacyExtensionFields(updatedData)
      updateNodeData(id, updatedData)
    },
    [id, data, currentExtensions, updateNodeData],
  )

  const handleRemoveExtension = useCallback(
    (extensionId: string) => {
      const newExtensions = currentExtensions.filter(
        (ext) => ext !== extensionId,
      )

      const updatedData = {
        ...data,
        extensions: newExtensions.length > 0 ? newExtensions : undefined,
      }
      cleanupLegacyExtensionFields(updatedData)
      updateNodeData(id, updatedData)
    },
    [id, data, currentExtensions, updateNodeData],
  )

  const handleTimeoutChange = useCallback((value: string) => {
    isUpdatingRef.current = true
    setTimeoutValue(value)
  }, [])

  const handleTimeoutBlur = useCallback(() => {
    const timeoutValue = timeout ? parseInt(timeout, 10) : undefined
    // Clamp to valid range: 5-600
    const clampedValue =
      timeoutValue && !isNaN(timeoutValue)
        ? Math.max(5, Math.min(600, timeoutValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      timeout: clampedValue || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, timeout])

  return {
    currentExtensions,
    timeout,
    handleExtensionChange,
    handleAddExtension,
    handleRemoveExtension,
    handleTimeoutChange,
    handleTimeoutBlur,
    hasData: currentExtensions.length > 0,
  }
}
